import React, { useState, useEffect } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import { 
  faPlus, 
  faFilm,
  faFolder,
  faCalendar,
  faFileText,
  faArrowRight,
  faTimes,
  faRocket,
  faStar,
  faProjectDiagram,
  faEllipsisVertical,
  faTrash,
  faCopy,
  faDownload,
  faVideo,
  faMicrophone,
  faImage,
  faHeadphones,
  faShare,
  faPen,
  faClosedCaptioning
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import Navigation from '../Home/Navigation';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { 
  fetchProjects, 
  Project as ProjectType, 
  deleteProject, 
  duplicateProject, 
  updateProjectStage,
  ProjectStage
} from '../../services/projectService';
import styles from './ScriptLandingPage.module.css';

interface Project {
  id: string;
  title: string;
  type: string;
  description?: string;
  createdAt: string;
  hasScript: boolean;
  metadata?: any;
}

const ScriptLandingPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'script'>('all');
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { token, user } = useAuth();
  const { t } = useTranslation('scriptGenerator');

  useEffect(() => {
    // Only load projects if user is authenticated
    if (token && user) {
      loadProjects();
    } else {
      setLoading(false);
      showNotification({
        title: t('scriptGenerator.landingPage.authRequired'),
        message: t('scriptGenerator.landingPage.authRequiredMessage'),
        type: 'error'
      });
    }
  }, [token, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeActionDropdown) {
        setActiveActionDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeActionDropdown]);

  const loadProjects = async () => {
    if (!token || !user) {
      //console.warn('No authentication token or user found');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      //console.log('Loading all projects for user:', user.id);
      
      const response = await fetchProjects(1, 1000);
      
      if (response && 'projects' in response) {
        // Additional client-side filtering to ensure only user's projects are shown
        const userProjects = response.projects.filter((project: any) => {
          if (project.userId && project.userId !== user.id) {
            console.warn(`Project ${project.id} belongs to different user:`, project.userId, 'vs', user.id);
            return false;
          }
          return true;
        });

        //console.log(`Loaded ${userProjects.length} projects for user ${user.id}`);
        setProjects(userProjects);
      } else {
        //console.warn('No projects found in response');
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      showNotification({
        title: t('scriptGenerator.landingPage.error'),
        message: 'Failed to load projects. Please try again.',
        type: 'error'
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!token || !user) {
      showNotification({
        title: t('scriptGenerator.landingPage.authRequired'),
        message: 'Please log in to create projects',
        type: 'error'
      });
      return;
    }

    if (!newProject.title.trim()) {
      showNotification({
        title: t('scriptGenerator.landingPage.error'),
        message: 'Project title is required',
        type: 'error'
      });
      return;
    }

    setIsCreating(true);
    try {
      //console.log('Creating script project for user:', user.id);
      
      const response = await fetch(getApiUrl('/script-generator/create-project'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newProject.title.trim(),
          description: newProject.description.trim() || undefined,
          userId: user.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // showNotification({
        //   title: 'Success',
        //   message: 'Script project created successfully!',
        //   type: 'success'
        // });
        
        // Close modal first
        setIsModalOpen(false);
        
        // Navigate directly to the script generator with project ID
        navigate(`/script-generator/${result.data.projectId}/script-generator`);
      } else {
        throw new Error(result.message || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showNotification({
        title: t('scriptGenerator.landingPage.error'),
        message: t('scriptGenerator.landingPage.projectCreatedError'),
        type: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    // Navigate based on project type
    
        // For unknown types or general projects, navigate to script generator as fallback
        navigate(`/script-generator/${project.id}`);
    
  };

  const openModal = () => {
    setIsModalOpen(true);
    setNewProject({ title: '', description: '' });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewProject({ title: '', description: '' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get appropriate icon for project type
  const getProjectIcon = (projectType: string) => {
    switch (projectType) {
      case 'script':
        return faFilm;
      case 'prompt':
        return faPen;
      case 'caption':
        return faClosedCaptioning;
      case 'content':
        return faVideo;
      case 'thumbnail':
        return faImage;
      case 'title':
        return faFileText;
      case 'audio':
        return faMicrophone;
      case 'sound':
        return faHeadphones;
      case 'social':
        return faShare;
      default:
        return faProjectDiagram;
    }
  };

  // Get project type display name
  const getProjectTypeLabel = (projectType: string) => {
    switch (projectType) {
      case 'script':
        return 'Script';
      case 'prompt':
        return 'Prompt';
      case 'caption':
        return 'Caption';
      case 'content':
        return 'Content';
      case 'thumbnail':
        return 'Thumbnail';
      case 'title':
        return 'Title';
      case 'audio':
        return 'Audio';
      case 'sound':
        return 'Sound';
      case 'social':
        return 'Social';
      default:
        return 'Project';
    }
  };

  // Filter and search projects
  const filteredProjects = projects.filter(project => {
    // Check if project has script in metadata
    const hasScript = project.metadata?.hasScript || false;
    
    // Apply type filter
    const typeMatch = filterType === 'all' || (filterType === 'script' && hasScript);
    
    // Apply search filter
    const searchMatch = searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return typeMatch && searchMatch;
  });

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      setActiveActionDropdown(null);
      showNotification({
        title: t('scriptGenerator.landingPage.success'),
        message: t('scriptGenerator.landingPage.projectDeletedSuccess'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      showNotification({
        title: t('scriptGenerator.landingPage.error'),
        message: t('scriptGenerator.landingPage.projectDeletedError'),
        type: 'error'
      });
    }
  };

  // Handle project duplicate
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const duplicatedProject = await duplicateProject(projectId);
      setProjects(prev => [duplicatedProject, ...prev]);
      setActiveActionDropdown(null);
      showNotification({
        title: t('scriptGenerator.landingPage.success'),
        message: t('scriptGenerator.landingPage.projectDuplicatedSuccess'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error duplicating project:', error);
      showNotification({
        title: t('scriptGenerator.landingPage.error'),
        message: t('scriptGenerator.landingPage.projectDuplicatedError'),
        type: 'error'
      });
    }
  };

  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.scriptGenerator.title}
        description={seoConfig.scriptGenerator.description}
        keywords={seoConfig.scriptGenerator.keywords}
        url="https://kingdomstudio.pro/script-generator"
      />
      <Navigation />
      
      <main className={styles.main}>
        {/* Header Section */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faFilm} className={styles.titleIcon} />
            {t('scriptGenerator.landingPage.title')}
          </h1>
          <p className={styles.subtitle}>
            {t('scriptGenerator.landingPage.subtitle')}
          </p>
          
          {/* Action Buttons */}
          <div className={styles.headerActions}>
            <Button
              className={styles.createButton}
              onClick={openModal}
              leftSection={<FontAwesomeIcon icon={faPlus} />}
              size="lg"
            >
              {t('scriptGenerator.landingPage.createProject')}
            </Button>
          </div>
        </div>

        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('scriptGenerator.landingPage.yourProjects')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('scriptGenerator.landingPage.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${filterType === 'all' ? styles.active : ''}`}
                  onClick={() => setFilterType('all')}
                >
                  {t('scriptGenerator.landingPage.all')} ({projects.length})
                </button>
                <button 
                  className={`${styles.filterButton} ${filterType === 'script' ? styles.active : ''}`}
                  onClick={() => setFilterType('script')}
                >
                  {t('scriptGenerator.landingPage.withScript')} ({projects.filter(p => p.metadata?.hasScript).length})
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}></div>
              <p>{t('scriptGenerator.landingPage.loading')}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faFilm} className={styles.emptyIcon} />
              <h3>{projects.length === 0 ? t('scriptGenerator.landingPage.noProjects') : t('scriptGenerator.landingPage.noMatchingProjects')}</h3>
              <p>{projects.length === 0 ? t('scriptGenerator.landingPage.noProjectsMessage') : t('scriptGenerator.landingPage.noMatchingMessage')}</p>
              {projects.length === 0 && (
                <Button
                  className={styles.createButton}
                  onClick={openModal}
                  leftSection={<FontAwesomeIcon icon={faPlus} />}
                  size="lg"
                >
                  {t('scriptGenerator.landingPage.createProject')}
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.projectsGrid}>
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={styles.projectCard}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className={styles.projectHeader}>
                    <div className={styles.projectIconWrapper}>
                      <FontAwesomeIcon icon={getProjectIcon(project.type)} className={styles.projectIcon} />
                    </div>
                    <div className={styles.projectStatus}>
                      {project.metadata?.hasScript && project.metadata?.scriptCount ? (
                        <span className={styles.scriptCountLabel}>
                          <FontAwesomeIcon icon={faFileText} />
                          {project.metadata.scriptCount} {project.metadata.scriptCount === 1 ? t('scriptGenerator.landingPage.script') : t('scriptGenerator.landingPage.scripts')}
                        </span>
                      ) : project.metadata?.hasScript ? (
                        <span className={styles.scriptCountLabel}>
                          <FontAwesomeIcon icon={faFileText} />
                          Script Ready
                        </span>
                      ) : null}
                    </div>
                  </div>
                  
                  <div className={styles.projectContent}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    {project.description && (
                      <p className={styles.projectDescription}>{project.description}</p>
                    )}
                    
                    <div className={styles.projectMeta}>
                      <div className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendar} />
                        <span>{formatDate(project.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActionDropdown(project.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faCopy} /> {t('scriptGenerator.landingPage.duplicate')}
                          </button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> {t('scriptGenerator.landingPage.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.projectGlow}></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Project Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FontAwesomeIcon icon={faRocket} className={styles.modalTitleIcon} />
                {t('scriptGenerator.landingPage.newProject')}
              </h2>
              <button
                className={styles.modalCloseButton}
                onClick={closeModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Content */}
            <div className={styles.modalContent}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  {t('scriptGenerator.landingPage.projectName')} *
                </label>
                <input
                  type="text"
                  placeholder={t('scriptGenerator.landingPage.projectNamePlaceholder')}
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  className={styles.inputField}
                  autoFocus
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  {t('scriptGenerator.landingPage.description')}
                </label>
                <textarea
                  placeholder={t('scriptGenerator.landingPage.descriptionPlaceholder')}
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                  className={styles.textareaField}
                />
              </div>
            </div>

            {/* Actions */}
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={closeModal}
              >
                {t('scriptGenerator.landingPage.cancel')}
              </button>
              <button
                className={styles.modalCreateButton}
                onClick={handleCreateProject}
                disabled={isCreating || !newProject.title.trim()}
              >
                <FontAwesomeIcon icon={faPlus} />
                {isCreating ? t('scriptGenerator.landingPage.creating') : t('scriptGenerator.landingPage.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScriptLandingPage;